import {
  Box,
  Button,
  chakra,
  Grid,
  GridItem,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useForm, useFieldArray } from "react-hook-form";
import { Fragment, useState } from "react";
import { dimensionsKeys, filterActiveDimensionItems, showErrorToast } from "../../../../utils";
import { FormSelect, NumericFormInput } from "../../../../components/Forms";
import { Trash } from "@phosphor-icons/react";
import { useGetDimensions } from "../../../../hooks/query/dimensions";

const getEmptyEmissionsRow = (organizationStructure, dimensionItems) => {
  const row = {};
  dimensionsKeys.forEach((dimKey) => {
    if (organizationStructure.dimensions[dimKey]) {
      row[`${dimKey}Item`] = dimensionItems[`${dimKey}Item`] ?? "";
    }
  });
  row.tco2e = "";
  return row;
};

function EmissionsItemModal({ modalData, isOpen, onClose, onSubmit }) {
  const { t } = useTranslation("translation");
  const toast = useToast();
  const { organizationStructure, dimensionItems, editData } = modalData;
  const fieldArrayName = "emissionsItems";

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm({
    defaultValues: {
      [fieldArrayName]: [editData ? editData : getEmptyEmissionsRow(organizationStructure, dimensionItems)],
    },
  });

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });
  const fieldArrayValues = watch(fieldArrayName);

  const [changedRowIndex, setChangedRowIndex] = useState(-1);
  const [initialDimensionsData, setInitialDimensionsData] = useState(null);

  const activeDimensions = dimensionsKeys.filter(
    (dimKey) => organizationStructure.dimensions[dimKey] !== null
  );

  // Same dimension query logic as GridItemsSection
  const getDimensionsQueryParams = () => {
    const queryParams = {};
    dimensionsKeys.forEach((key) => {
      if (changedRowIndex !== -1) {
        if (
          fieldArrayValues[changedRowIndex] &&
          typeof fieldArrayValues[changedRowIndex][`${key}Item`] !== "undefined" &&
          fieldArrayValues[changedRowIndex][`${key}Item`] !== null &&
          fieldArrayValues[changedRowIndex][`${key}Item`] !== ""
        ) {
          queryParams[`${key}Item`] = fieldArrayValues[changedRowIndex][`${key}Item`];
        }
      } else {
        if (editData) {
          const dimItem = editData[`${key}Item`];
          if (dimItem !== null && typeof dimItem !== "undefined" && dimItem !== "") {
            queryParams[`${key}Item`] = dimItem;
          }
        } else {
          if (dimensionItems !== null) {
            if (dimensionItems[`${key}Item`] !== null && typeof dimensionItems[`${key}Item`] !== "undefined") {
              queryParams[`${key}Item`] = dimensionItems[`${key}Item`];
            }
          }
        }
      }
    });
    return queryParams;
  };

  useGetDimensions(getDimensionsQueryParams(), {
    onSuccess: (data) => {
      if (changedRowIndex !== -1) {
        update(changedRowIndex, {
          ...fieldArrayValues[changedRowIndex],
          dimensionsData: data,
        });
      } else {
        setInitialDimensionsData(data);
      }
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    enabled: changedRowIndex !== -1 || initialDimensionsData === null,
  });

  const renderDimensionOptions = (dimKey, rowIndex) => {
    let dimItems = [];
    if (fieldArrayValues[rowIndex] && fieldArrayValues[rowIndex].dimensionsData) {
      dimItems.push(...fieldArrayValues[rowIndex].dimensionsData[dimKey].items);
    } else {
      dimItems.push(...initialDimensionsData[dimKey].items);
    }
    const currentValue = fieldArrayValues[rowIndex]?.[`${dimKey}Item`];
    return filterActiveDimensionItems(dimItems, currentValue).map((item) => (
      <option key={`${dimKey}Item-${rowIndex}-${item.id}`} value={item.id}>
        {item.name}
      </option>
    ));
  };

  // Column count: dimensions * 2 + tCO2e * 2 + action col (if adding)
  const colCount = (activeDimensions.length + 1) * 2 + (editData ? 0 : 1);

  const headerTitles = activeDimensions.map(
    (dimKey) => organizationStructure.dimensions[dimKey].name
  );
  headerTitles.push("tCO2e");
  if (!editData) headerTitles.push("");

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="lg"
        w="fit-content"
        maxW="calc(100vw - 32px)"
        minW={{ base: "auto", md: "640px" }}
      >
        <chakra.form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader fontSize="18px" fontWeight="700" color="darkTeal">
            {t(`organizationQuestionnaire.${editData ? "editItemModalHeader" : "addItemsModalHeader"}`, { type: "emissions" })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white">
              <Grid
                templateColumns={`repeat(${colCount}, auto)`}
                minWidth="700"
                maxHeight="400"
                overflow="auto"
                sx={{
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
                  "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
                  scrollbarWidth: "thin",
                  scrollbarColor: "transparent transparent",
                  "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
                }}
              >
                {/* Header */}
                {headerTitles.map((title) => (
                  <GridItem
                    key={`heading-emissions-${title}`}
                    px={4}
                    py={3}
                    display="flex"
                    alignItems="center"
                    bg="gray.50"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    colSpan={editData ? 2 : title !== "" ? 2 : 1}
                  >
                    <Text
                      fontSize="13px"
                      fontWeight="600"
                      color="gray.500"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {title}
                    </Text>
                  </GridItem>
                ))}

                {/* Rows */}
                {initialDimensionsData !== null && (
                  <Fragment>
                    {fields.map((field, index) => {
                      const gridItems = [];
                      const totalCols = activeDimensions.length + 1 + (editData ? 0 : 1);

                      // Dimension fields
                      dimensionsKeys.forEach((dimKey) => {
                        if (organizationStructure.dimensions[dimKey]) {
                          if (dimensionItems[`${dimKey}Item`] !== null) {
                            // Predefined — show as disabled label
                            gridItems.push(
                              <FormSelect
                                {...register(`${fieldArrayName}.${index}.${dimKey}Item`)}
                                onMouseDown={(e) => e.preventDefault()}
                                borderWidth="0"
                                margin="0"
                                whiteSpace="nowrap"
                                padding="0"
                                width="100%"
                                style={{ padding: 0 }}
                                fontWeight="semibold"
                                fontSize="sm"
                                color="black"
                                icon={<span />}
                                title={organizationStructure.dimensions[dimKey].items.find(
                                  (item) => item.id.toString() === fieldArrayValues[index]?.[`${dimKey}Item`]?.toString()
                                )?.name ?? ""}
                              >
                                {organizationStructure.dimensions[dimKey].items.map((optItem) => (
                                  <option key={`${dimKey}Item-${index}-${optItem.id}`} value={optItem.id}>
                                    {optItem.name}
                                  </option>
                                ))}
                              </FormSelect>
                            );
                          } else {
                            // User-selectable dropdown
                            const dimSelector = {
                              ...register(`${fieldArrayName}.${index}.${dimKey}Item`),
                            };
                            gridItems.push(
                              <FormSelect
                                {...register(`${fieldArrayName}.${index}.${dimKey}Item`)}
                                fontSize="sm"
                                fontWeight="semibold"
                                errors={errors}
                                onChange={(e) => {
                                  dimSelector.onChange(e);
                                  setChangedRowIndex(index);
                                }}
                                title={organizationStructure.dimensions[dimKey].items.find(
                                  (item) => item.id.toString() === fieldArrayValues[index]?.[`${dimKey}Item`]?.toString()
                                )?.name ?? ""}
                              >
                                <option value="">{""}</option>
                                {renderDimensionOptions(dimKey, index)}
                              </FormSelect>
                            );
                          }
                        }
                      });

                      // tCO2e field
                      gridItems.push(
                        <NumericFormInput
                          {...register(`${fieldArrayName}.${index}.tco2e`, { required: t("errors.required") })}
                          fontSize="sm"
                          fontWeight="semibold"
                          errors={errors}
                          placeholder="0.00"
                          title={fieldArrayValues[index]?.tco2e ?? ""}
                        />
                      );

                      // Remove row button (add mode only)
                      if (!editData) {
                        gridItems.push(
                          <IconButton
                            type="button"
                            icon={<Trash size={18} />}
                            variant="ghost"
                            color="gray.500"
                            _hover={{ color: "red.500" }}
                            disabled={fields.length === 1}
                            aria-label="Remove row"
                            onClick={() => {
                              setChangedRowIndex(-1);
                              remove(index);
                            }}
                          />
                        );
                      }

                      return gridItems.map((gridItem, gridItemIndex) => (
                        <GridItem
                          key={`emissions-${index}-${gridItemIndex}`}
                          px={4}
                          py={2}
                          display="flex"
                          alignItems="center"
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          colSpan={editData ? 2 : gridItemIndex === totalCols - 1 ? 1 : 2}
                        >
                          {gridItem}
                        </GridItem>
                      ));
                    })}

                    {/* Add another row */}
                    {!editData && (
                      <GridItem
                        colSpan={colCount}
                        px={4}
                        py={3}
                        display="flex"
                        alignItems="center"
                        bg="lightLime"
                        cursor="pointer"
                        onClick={() => {
                          append(getEmptyEmissionsRow(organizationStructure, dimensionItems), { shouldFocus: false });
                        }}
                        _hover={{ bg: "limeShade.50" }}
                      >
                        <Text fontSize="14px" fontWeight="600" color="limeShade.700">
                          + {t("buttons.addAnotherItem")}
                        </Text>
                      </GridItem>
                    )}
                  </Fragment>
                )}
              </Grid>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="full">
              {t("buttons.close")}
            </Button>
            <Button bg="lime" color="pureBlack" borderRadius="full" _hover={{ bg: "limeShade.300" }} type="submit">
              {t("buttons.save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>
  );
}

export { getEmptyEmissionsRow };
export default EmissionsItemModal;
